import { config } from "@repo/config";
import { OrganizationInvitationModal } from "@saas/organizations/components/OrganizationInvitationModal";
import { AuthWrapper } from "@saas/shared/components/AuthWrapper";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

const getInvitationFn = createServerFn({ method: "GET" })
	.validator((data: { invitationId: string }) => data)
	.handler(async ({ data }) => {
		// Dynamic imports to prevent server code from being bundled for client
		const [{ auth, authApi }, { getInvitationById, getUserByEmail }] =
			await Promise.all([import("@repo/auth"), import("@repo/database")]);
		const headers = getRequest().headers;

		// Better Auth rejects invitations that expired, were revoked or were
		// sent to another address. Show that as a state, not a crash.
		const invitation = await authApi
			.getInvitation({ query: { id: data.invitationId }, headers })
			.catch(() => null);

		if (invitation) {
			const organization = await getInvitationById(data.invitationId);
			return {
				valid: true as const,
				organizationName: invitation.organizationName,
				organizationSlug: invitation.organizationSlug,
				logoUrl: organization?.organization.logo ?? undefined,
				role: invitation.role,
				invitationId: data.invitationId,
			};
		}

		// Still open but sent to someone else: offer to switch accounts rather
		// than a dead end (people open the link while signed in as another email).
		const [pending, session] = await Promise.all([
			getInvitationById(data.invitationId),
			auth.api.getSession({ headers }),
		]);
		if (
			pending?.status !== "pending" ||
			pending.expiresAt.getTime() < Date.now() ||
			pending.email.toLowerCase() === session?.user.email.toLowerCase()
		) {
			return { valid: false as const };
		}
		const hasAccount = Boolean(
			await getUserByEmail(pending.email.toLowerCase()),
		);
		const search = new URLSearchParams({
			invitationId: pending.id,
			email: pending.email,
		});
		return {
			valid: true as const,
			invitationId: pending.id,
			invitedEmail: pending.email,
			organizationName: pending.organization.name,
			logoUrl: pending.organization.logo ?? undefined,
			// The same pages the invitation email links to.
			continueTo: `${hasAccount ? "/login" : "/signup"}?${search}`,
		};
	});

export const Route = createFileRoute(
	"/_saas/organization-invitation/$invitationId",
)({
	loader: ({ params }) =>
		getInvitationFn({ data: { invitationId: params.invitationId } }),
	head: () => ({
		meta: [{ title: `Your invitation - ${config.appName}` }],
	}),
	component: OrganizationInvitationPage,
});

function OrganizationInvitationPage() {
	const invitation = Route.useLoaderData();
	const { session } = Route.useRouteContext();

	return (
		<AuthWrapper>
			<OrganizationInvitationModal
				{...(invitation.valid ? invitation : { invitationId: null })}
				signedInAs={session.user.email}
			/>
		</AuthWrapper>
	);
}
